import { Link } from "react-router-dom";
import { FileText, ScrollText, Wallet, Cookie, ShieldCheck, Mail, ArrowRight } from "lucide-react";
import PublicPageHeader from "@/components/PublicPageHeader";
import SiteFooter from "@/components/SiteFooter";

const DOCUMENTS = [
  { icon: FileText, title: "Privacy Policy", desc: "Learn how we collect, use, store, and protect your personal and medical information.", to: "/privacy-policy", testId: "legal-link-privacy" },
  { icon: ScrollText, title: "Terms & Conditions", desc: "Read the terms governing your access to and use of the RootLyst platform.", to: "/terms-and-conditions", testId: "legal-link-terms" },
  { icon: Cookie, title: "Cookie Policy", desc: "Understand what cookies RootLyst uses and why they're essential to the platform.", to: "/cookie-policy", testId: "legal-link-cookies" },
  { icon: Wallet, title: "Refund & Cancellation Policy", desc: "Review our subscription cancellation and refund guidelines.", to: "/refund-policy", testId: "legal-link-refund" },
];

export default function LegalPage() {
  return (
    <div className="bg-slate-50 text-slate-900">
      <PublicPageHeader />
      <main className="mx-auto max-w-3xl px-4 py-14 sm:px-6" data-testid="legal-hub-page">
        <h1 className="text-3xl font-semibold text-slate-900 sm:text-4xl">Legal</h1>
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
          <span>Effective Date: 16/07/2026</span>
          <span>Last Updated: 16/07/2026</span>
        </div>
        <p className="mt-6 text-sm leading-relaxed text-slate-600 sm:text-base">
          Welcome to RootLyst Legal Center. This page provides access to all legal policies and important information
          governing the use of the RootLyst platform.
        </p>

        <h2 className="mt-10 text-lg font-semibold text-slate-900">Available Legal Documents</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {DOCUMENTS.map((d) => (
            <Link
              key={d.title}
              to={d.to}
              data-testid={d.testId}
              className="card-surface card-surface-hover flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
                <d.icon className="h-4 w-4" />
              </div>
              <h3 className="mt-3 font-semibold text-slate-900">{d.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600">{d.desc}</p>
              <span className="mt-3 flex items-center gap-1 text-sm font-medium text-teal-700">
                View {d.title} <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          ))}
        </div>

        <section className="mt-10" data-testid="legal-ai-notice">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <ShieldCheck className="h-4 w-4 text-teal-700" /> AI Notice
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">
            RootLyst uses Artificial Intelligence (AI) to assist healthcare professionals with report analysis,
            patient summaries, timelines, and clinical insights. AI-generated content is intended only as a
            clinical support tool and should always be reviewed by a qualified healthcare professional before
            making medical decisions.
          </p>
        </section>

        <section className="mt-10" data-testid="legal-data-security">
          <h2 className="text-lg font-semibold text-slate-900">Data Security</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">RootLyst is committed to protecting your data through:</p>
          <ul className="mt-3 list-inside list-disc space-y-1.5 text-sm text-slate-600 sm:text-base">
            <li>Secure encrypted connections (HTTPS)</li>
            <li>Password hashing</li>
            <li>Role-based access control</li>
            <li>Secure cloud infrastructure</li>
            <li>Regular security monitoring</li>
          </ul>
        </section>

        <section className="mt-10" data-testid="legal-contact">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <Mail className="h-4 w-4 text-teal-700" /> Contact
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">
            If you have questions regarding any legal policy, please contact:
            <br />RootLyst
            <br /><a href="mailto:contact@rootlyst.com" className="font-medium text-teal-700 hover:underline">contact@rootlyst.com</a>
          </p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
